import { useRef, type ReactNode } from "react";

import {
  AppDialog,
  AppDialogActions,
  DialogPrimaryButton,
  DialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { dismissGuard } from "@/components/ui/dismiss-guard";

// The donor's "are you sure" shape, as one shared Atlas component: react-ui's
// `Dialog` with a `DialogActions` row of `SecondaryDialogButton` (Cancel) and
// `PrimaryDialogButton` (the verb) — which the donor composes by hand in each
// confirmation (`confirm-delete-dialog.tsx`, `delete-item-dialog.tsx`,
// `mark-core-item-dialog.tsx`).
//
// Atlas had grown the shell twice: `ConfirmBatchDialog` in the catalog's batch
// dialogs, and `ConfirmDeleteDialog` beside it, each with its own footer, its
// own opening focus and its own answer to a dismissal mid-write. The body is
// what they do not share — a question, a list of names, a form field — so it
// stays the caller's, and this carries only the frame around it.
//
// **Dismissal mid-write is refused by default — a named, owner-ruled parity
// break** (#458 decision round, D1: "Keep the guard"). Every donor confirmation
// passes `onClose` unconditionally (`delete-item-dialog.tsx:47`,
// `mark-core-item-dialog.tsx:88,135`, `confirm-delete-dialog.tsx:26`), so a
// modsy user can dismiss over a running write and take its refusal off the
// screen with nothing to recall. Escape, the overlay and the corner X all
// arrive at `onOpenChange`, not at Cancel, so the guard sits here. The ruling
// covers the batch and card-menu confirmations; the details page's shared
// delete confirmation keeps the donor's live dismissal
// (`dismissibleWhileSubmitting`), as it did before this shell existed.
//
// **The opening focus is taken from Radix on every dialog.** Radix focuses the
// first tabbable child; MUI's focus trap focuses a child that carries
// `autoFocus`, and otherwise the paper itself. So a donor dialog with an
// `autoFocus` confirm opens on the verb, and one without opens on nothing a
// press of Enter would act on — where Radix alone would have opened on Cancel.

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** The verb's own label — "Delete", "Mark as Core". */
  confirmLabel: string;
  /** A fixed accessible name, where the donor's button carries an
   *  `aria-label` its changing label does not follow. */
  confirmAriaLabel?: string;
  /** Called with no arguments, never with React's click event. */
  onConfirm: () => void;
  /** A write in flight: the verb loads, Cancel is held, dismissal refused. */
  isSubmitting?: boolean;
  /** The verb held shut for a reason that is not the write. */
  confirmDisabled?: boolean;
  /** Open on the verb — the donor's `autoFocus` on its primary button. */
  autoFocusConfirm?: boolean;
  /** MUI's `fullWidth` — see `AppDialog`; on by default. */
  fullWidth?: boolean;
  /** Let Escape, the overlay and the X close the dialog mid-write. */
  dismissibleWhileSubmitting?: boolean;
  /** The id of the body element the donor names as the dialog's description. */
  describedById?: string;
  /** See `AppDialog` — where the focus goes on closing. */
  onCloseAutoFocus?: (event: Event) => void;
  /** See `AppDialog` — the close as an unmount, with no fade. */
  closeInstantly?: boolean;
  cancelTestId?: string;
  confirmTestId?: string;
  /** The question, and whatever it needs beside it. */
  children: ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  confirmLabel,
  confirmAriaLabel,
  onConfirm,
  isSubmitting = false,
  confirmDisabled = false,
  autoFocusConfirm = false,
  fullWidth = true,
  dismissibleWhileSubmitting = false,
  describedById,
  onCloseAutoFocus,
  closeInstantly,
  cancelTestId,
  confirmTestId,
  children,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  // The scope's own container is `tabIndex={-1}` and the event is dispatched
  // on it, so `currentTarget` is the paper — focusing it is MUI's fallback.
  const takeOpeningFocus = (event: Event) => {
    event.preventDefault();
    if (autoFocusConfirm) confirmRef.current?.focus();
    else (event.currentTarget as HTMLElement | null)?.focus();
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={
        dismissibleWhileSubmitting ? onOpenChange : dismissGuard(onOpenChange, isSubmitting)
      }
      title={title}
      // MUI's `maxWidth='sm'`, 600px, which every donor confirmation asks for.
      size="sm"
      fullWidth={fullWidth}
      describedById={describedById}
      onOpenAutoFocus={takeOpeningFocus}
      onCloseAutoFocus={onCloseAutoFocus}
      closeInstantly={closeInstantly}
      actions={
        <AppDialogActions>
          <DialogSecondaryButton
            data-testid={cancelTestId}
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </DialogSecondaryButton>
          {/* MUI's `LoadingButton`, which disables itself while it loads, so
              `disabled` carries only the reasons that are not the write. */}
          <DialogPrimaryButton
            ref={confirmRef}
            data-testid={confirmTestId}
            aria-label={confirmAriaLabel}
            loading={isSubmitting}
            disabled={confirmDisabled}
            // Wrapped, not passed: handed straight to `onClick` the verb would
            // receive React's click event as its first argument.
            onClick={() => onConfirm()}
          >
            {confirmLabel}
          </DialogPrimaryButton>
        </AppDialogActions>
      }
    >
      {children}
    </AppDialog>
  );
}

/** A whole-write failure, drawn inside the dialog and announced. */
export function ConfirmDialogFailure({ error }: { error?: string | null }) {
  if (!error) return null;
  return (
    <p role="alert" className="text-lennar-error">
      {error}
    </p>
  );
}
